import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Mobile, Select } from '@interfaces';
import { NewContractPymesService } from 'src/app/services/new-contract-pymes.service';
import { UtilService } from 'src/app/services/util.service';
import { BusinessMultiPoint } from '../../new-contract-pymes-multipoint.model';
import { NewContractPymesMultipointService } from '../../new-contract-pymes-multipoint.service';
import { UtilValidatorsService } from 'src/app/commons/validations-format/util-validators.service';
import { AuthService } from 'src/app/auth';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';

@Component({
  selector: 'app-multipoint-business-name-pymes',
  templateUrl: './multipoint-business-name-pymes.component.html',
  styleUrls: ['./multipoint-business-name-pymes.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MultipointBusinessNamePymesComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  public businessNameForm: FormGroup;
  public roadTypeList: Select[] = [];
  public townList: Select[] = [];
  public approved = false;
  public callQuery = false;
  private canEdit = false;
  private idSocialReason: string;
  private codeProvince: string;
  public isUserSpanish = true;
  public mobileCountryCodeList: Select[];

  @Output() changeLoader: EventEmitter<boolean> = new EventEmitter();
  @Output() data: EventEmitter<BusinessMultiPoint> = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    private newContractService: NewContractPymesService,
    private utilService: UtilService,
    private newContractPymesMultipointService: NewContractPymesMultipointService,
    private utilValidatorsService: UtilValidatorsService,
    private authService: AuthService,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
  ) { }

  ngOnInit(): void {
    this.isUserSpanish = this.authService.getUserData().country === 'ES';
    this.changeLoader.emit(true);
    this.createForm();
    this.getValuesForm();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private createForm(): void {
    this.businessNameForm = this.formBuilder.group({
      socialReasonLabel: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(70)]],
      numberDocument: [{ value: '', disabled: true }, [Validators.required, this.utilValidatorsService.validateCIFNIF]],
      cnae: [
        { value: '', disabled: true },
        [
          Validators.required,
          Validators.maxLength(this.isUserSpanish ? 4 : 5),
          this.utilValidatorsService.validateCNAE
        ]
      ],
      contactName: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(50)]],
      surname: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(50)]],
      dni: [{ value: '', disabled: true }, this.utilValidatorsService.validateDNI],
      email: [
        { value: '', disabled: true },
        [Validators.required, Validators.pattern(this.utilValidatorsService.emailPattern), Validators.maxLength(50)]
      ],
      mobileCountryCode: [{ value: '', disabled: true }],
      mobile: [{ value: '', disabled: true }, [this.utilValidatorsService.validateMobile, Validators.required, Validators.maxLength(9)]],
      roadType: [{ value: '', disabled: true }, Validators.required],
      roadName: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]],
      addressNumber: [{ value: '', disabled: true }, [Validators.maxLength(8), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      gate: [{ value: '', disabled: true }, Validators.maxLength(10)],
      stair: [{ value: '', disabled: true }, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      floor: [{ value: '', disabled: true }, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      door: [{ value: '', disabled: true }, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      postalCode: [{ value: '', disabled: true }],
      postalCodePT1: [{ value: '', disabled: true }],
      postalCodePT2: [{ value: '', disabled: true }],
      town: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]],
      province: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]]
    });
    this.initPostalCodePT();
    this.initMobileCountryCodeConfigPortugal();
  }

  private getValuesForm(): void {
    this.newContractPymesMultipointService.getSavedDataMultipoint().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      this.canEdit = !!res?.business?.canEdit;
      this.approved = !!res?.approved;
      this.callQuery = res?.approved !== undefined;
      const mobileAndCountryCode = this.getMobileAndCountryCode(res?.business?.mobile);
      this.businessNameForm.patchValue({
        socialReasonLabel: res?.business?.socialReasonLabel || '',
        numberDocument: res?.business?.numberDocument || '',
        cnae: res?.business?.cnae || '',
        contactName: res?.business?.contactName || '',
        surname: res?.business?.surname || '',
        dni: res?.business?.dni || '',
        email: res?.business?.email || '',
        mobileCountryCode: this.isUserSpanish ? null : mobileAndCountryCode.countryCode,
        mobile: mobileAndCountryCode.mobile || '',
        roadType: res?.business?.roadType || null,
        roadName: res?.business?.roadName || '',
        addressNumber: res?.business?.addressNumber || '',
        gate: res?.business?.gate || '',
        stair: res?.business?.stair || '',
        floor: res?.business?.floor || '',
        door: res?.business?.door || '',
        postalCode: res?.business?.postalCode || '',
        postalCodePT1: this.isUserSpanish ? null : res?.business?.postalCode?.slice(0, 4) || '',
        postalCodePT2: this.isUserSpanish ? null : res?.business?.postalCode?.slice(5, 8) || '',
        town: res?.business?.town || '',
        province: res?.business?.province || ''
      });
      this.idSocialReason = res?.business?.socialReason || null;
      this.destroyed$.next();
      this.destroyed$.complete();
      this.enableForm();
      this.subscribeFormControl();
      this.getRoadTypes();
      if (res?.business?.postalCode) {
        this.getPostalCode(res?.business?.postalCode);
      }
      this.emitChanges();
    });
  }

  private enableForm(): void {
    if (this.callQuery) {
      if (this.canEdit) {
        this.businessNameForm.get('socialReasonLabel').enable({ emitEvent: false });
        this.businessNameForm.get('cnae').enable({ emitEvent: false });
        this.businessNameForm.get('contactName').enable({ emitEvent: false });
        this.businessNameForm.get('surname').enable({ emitEvent: false });
        this.businessNameForm.get('dni').enable({ emitEvent: false });
        this.businessNameForm.get('email').enable({ emitEvent: false });
        if (this.isUserSpanish) {
          this.businessNameForm.get('mobile').enable({ emitEvent: false });
        } else {
          this.businessNameForm.get('mobileCountryCode').enable({ emitEvent: false });
        }
        this.businessNameForm.get('roadType').enable({ emitEvent: false });
        this.businessNameForm.get('roadName').enable({ emitEvent: false });
        this.businessNameForm.get('addressNumber').enable({ emitEvent: false });
        this.businessNameForm.get('gate').enable({ emitEvent: false });
        this.businessNameForm.get('stair').enable({ emitEvent: false });
        this.businessNameForm.get('floor').enable({ emitEvent: false });
        this.businessNameForm.get('door').enable({ emitEvent: false });
        this.businessNameForm.get('postalCode').enable({ emitEvent: false });
        this.businessNameForm.get('postalCodePT1').enable({ emitEvent: false });
        this.businessNameForm.get('postalCodePT2').enable({ emitEvent: false });
      }
    } else {
      this.businessNameForm.get('numberDocument').enable({ emitEvent: false });
    }
    this.businessNameForm.updateValueAndValidity();
  }

  private subscribeFormControl(): void {
    this.businessNameForm.get('postalCode').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.businessNameForm.get('town').setValue(null);
      this.businessNameForm.get('province').setValue(null);
      if (value && this.isValidPostalCode()) {
        this.getPostalCode(value);
      }
    });
    this.businessNameForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.emitChanges();
    });
    this.subscribeFormControlPortugal();
  }

  private getPostalCode(value: string): void {
    this.changeLoader.emit(true);
    this.newContractService.getCodigoPostal(value).pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        if (res.success) {
          this.townList = res.data.poblaciones ?
            res.data.poblaciones.map(town => ({ label: town.nombrePoblacion, value: town.codigoPoblacion })) : [];
          if (this.townList.length <= 1) {
            this.businessNameForm.get('town').setValue(this.townList[0]?.label);
            this.businessNameForm.get('town').disable();
          } else if (this.canEdit || (this.approved && this.callQuery && this.canEdit)) {
            this.businessNameForm.get('town').enable();
          }
          if (res.data?.nombreProvincia) {
            this.businessNameForm.get('province').setValue(res.data?.nombreProvincia);
            this.codeProvince = res.data.codigoProvincia;
          }
          this.businessNameForm.updateValueAndValidity();
          this.changeLoader.emit(false);
        } else {
          this.townList = [];
          this.changeLoader.emit(false);
          this.utilService.toastErrors(res);
        }
      },
      error => {
        this.townList = [];
        this.changeLoader.emit(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  private getRoadTypes(): void {
    this.newContractService.getTipoVia().pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        if (res.success) {
          this.roadTypeList = res.data.map(road => ({ label: road.nombreTipoVia, value: road.nombreTipoVia }));
        } else {
          this.roadTypeList = [];
          this.utilService.toastErrors(res);
        }
        this.changeLoader.emit(false);
      },
      error => {
        this.roadTypeList = [];
        this.changeLoader.emit(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  public handleQuery(): void {
    this.changeLoader.emit(true);
    this.newContractPymesMultipointService.queryCifMultiPoint(
      this.businessNameForm.get('numberDocument').value,
      this.newContractPymesMultipointService.getSavedDataMultipoint().value.contactCRM
    ).pipe(takeUntil(this.destroyed$)).subscribe(
      response => {
        if (response.success) {
          this.callQuery = true;
          this.approved = response.data.aprobado;
          this.canEdit = response.data.editable;
          this.newContractPymesMultipointService.setSavedDataMultipoint({ idDraft: response.data.idBorrador, approved: this.approved });
          if (this.approved) {
            this.idSocialReason = response.data.idRazonSocial;
            const mobileAndCountryCode = this.getMobileAndCountryCode(response.data.telefonoContactoComercial);
            this.businessNameForm.patchValue({
              socialReasonLabel: response.data.nombre || '',
              cnae: response.data.cnae || '',
              contactName: response.data.nombreContactoComercial || '',
              surname: response.data.apellidosContactoComercial || '',
              dni: response.data.dniContactoComercial || '',
              email: response.data.mailContactoComercial || '',
              mobileCountryCode: this.isUserSpanish ? null : mobileAndCountryCode.countryCode,
              mobile: mobileAndCountryCode.mobile || '',
              roadType: response.data.nombreTipoViaDireccion || null,
              roadName: response.data.nombreDireccion || '',
              addressNumber: response.data.numeroDireccion || '',
              gate: response.data.portalDireccion || '',
              stair: response.data.escaleraDireccion || '',
              floor: response.data.pisoDireccion || '',
              door: response.data.puertaDireccion || '',
              postalCode: response.data.codPostalDireccion || '',
              postalCodePT1: this.isUserSpanish ? null : response.data?.codPostalDireccion?.slice(0, 4) || '',
              postalCodePT2: this.isUserSpanish ? null : response.data?.codPostalDireccion?.slice(5, 8) || '',
              town: response.data.codigoPoblacionDireccion || '',
              province: response.data.codigoProvinciaDireccion || ''
            });
            this.businessNameForm.get('numberDocument').disable({ emitEvent: false });
            this.businessNameForm.get('cnae').enable({ emitEvent: false });
            this.enableForm();
          } else {
            Object.keys(this.businessNameForm.controls).forEach(element => {
              this.businessNameForm.get(element).disable({ emitEvent: false });
            });
          }
          this.businessNameForm.updateValueAndValidity();
        } else {
          this.callQuery = false;
          this.approved = false;
          this.newContractPymesMultipointService.setSavedDataMultipoint({ idBorrador: null });
          this.utilService.toastErrors(response);
        }
        this.changeLoader.emit(false);
      },
      error => {
        this.callQuery = false;
        this.approved = false;
        this.newContractPymesMultipointService.setSavedDataMultipoint({ idBorrador: null });
        this.utilService.toastErrors(error);
        this.changeLoader.emit(false);
      }
    );
  }

  private emitChanges(): void {
    const data: BusinessMultiPoint = {
      canGoNext: (this.businessNameForm.valid || this.businessNameForm.disabled) &&
        this.businessNameForm.get('town').value && this.businessNameForm.get('province').value && this.approved,
      canSave: this.approved,
      canEdit: this.canEdit,
      socialReason: this.idSocialReason,
      ...this.businessNameForm.getRawValue()
    };
    if (!this.isUserSpanish) {
      data.mobile = this.businessNameForm.get('mobileCountryCode').value + data.mobile;
    }
    if (this.townList?.length === 1) {
      data.town = this.townList[0]?.value;
      data.townLabel = this.townList[0]?.label;
    } else {
      data.townLabel = this.townList.find((value: Select) => value.value === this.businessNameForm.get('town').value)?.label;
    }
    data.provinceLabel = this.businessNameForm.get('province').value;
    data.province = this.codeProvince;
    this.data.emit(data);
  }

  private initPostalCodePT(): void {
    if (this.isUserSpanish) {
      this.businessNameForm.get('postalCode').setValidators([Validators.required, Validators.minLength(5)]);
    } else {
      this.businessNameForm.get('postalCodePT1').setValidators([Validators.required, Validators.maxLength(4)]);
      this.businessNameForm.get('postalCodePT2').setValidators([Validators.required, Validators.maxLength(3)]);
      this.businessNameForm.get('postalCode').setValidators([
        Validators.required,
        Validators.pattern(this.utilValidatorsService.postalCodePTPattern)
      ]);
    }
  }

  private subscribeFormControlPortugal(): void {
    if (!this.isUserSpanish) {
      this.businessNameForm.get('postalCodePT1').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.businessNameForm.get('postalCode').setValue(value + '-' + this.businessNameForm.get('postalCodePT2').value);
      });
      this.businessNameForm.get('postalCodePT2').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.businessNameForm.get('postalCode').setValue(this.businessNameForm.get('postalCodePT1').value + '-' + value);
      });

      this.businessNameForm.get('mobileCountryCode').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
        value => {
          if (value) {
            if (this.canEdit) {
              this.businessNameForm.get('mobile').enable();
            }
            this.mobileCountryCodeChange(value);
          }
        }
      );
    }
  }

  private isValidPostalCode(): boolean {
    if (this.isUserSpanish) {
      return this.businessNameForm.get('postalCode').value?.length > 4;
    } else {
      return this.businessNameForm.get('postalCodePT1').value?.length === 4 &&
        this.businessNameForm.get('postalCodePT2').value?.length === 3 &&
        this.businessNameForm.get('postalCode').value?.length === 8;
    }
  }

  private mobileCountryCodeChange(value: string): void {
    if (value === '0034') {
      this.businessNameForm.get('mobile').setValidators(
        [this.utilValidatorsService.validateMobile, Validators.required, Validators.maxLength(9)]
      );
    } else if (value === '00351') {
      this.businessNameForm.get('mobile').setValidators(
        [this.utilValidatorsService.validateMobilePT, Validators.required, Validators.maxLength(9)]
      );
    }
    this.businessNameForm.get('mobile').updateValueAndValidity({ onlySelf: true });
  }

  private getMobileAndCountryCode(value: string | null): Mobile {
    const result: Mobile = { mobile: value };
    if (value) {
      if (!this.isUserSpanish && value?.length > 9) {
        const countryCode = value.slice(0, value.length - 9);
        const validCountryCode = this.mobileCountryCodeList.find(code => code.value === countryCode);
        if (validCountryCode) {
          result.countryCode = countryCode;
        }
      }
      result.mobile = value.slice(value.length - 9, value.length);
    }

    return result;
  }

  private initMobileCountryCodeConfigPortugal(): void {
    if (!this.isUserSpanish) {
      this.businessNameForm.get('mobileCountryCode').setValidators(Validators.required);
      this.i18NextService.events.languageChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.mobileCountryCodeList = this.utilService.getListCountryMobile();
      });
    }
  }
}
